import { useCallback, useEffect, useRef, useState } from 'react'
import {
  outcomeImportsApi,
  type OperatorOutcomeImport,
  type OutcomeAnomalyRow,
  type OutcomeImportPreview,
} from '../api/contracts'
import { useFeatureFlag } from '../lib/featureFlags'
import { CSV_ACCEPT, EXCEL_ACCEPT, readTabularFile } from '../lib/spreadsheet'
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  DataTable,
  Dialog,
  FormGrid,
  IconButton,
  LoadingState,
  Page,
  PageHeader,
  StatusPill,
  Tabs,
  TextField,
  type PillTone,
} from '../components/ui'

function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

function fmtDateTime(s: string | undefined): string {
  if (!s) return '—'
  return new Date(s).toLocaleString('it-IT')
}

/**
 * Direct fetch for CSV text (POST /api/contracts/outcome-imports).
 * Cannot use api<T>() from client.ts because it always sets Content-Type: application/json,
 * but the CSV endpoint semantically expects text/plain. The controller reads Request.Body
 * directly (no [FromBody]), so the Content-Type is informational, but we respect the semantic.
 * Error handling mirrors api<T>() for consistency.
 */
async function createOutcomeImportPreview(csvText: string): Promise<OutcomeImportPreview> {
  const headers = new Headers()
  headers.set('Content-Type', 'text/plain')

  // Questa e' l'unica scrittura dell'app che non passa da api<T>() — serve perche' quello imposta
  // sempre Content-Type: application/json e qui il corpo e' CSV grezzo. Il token antiforgery pero'
  // va mandato lo stesso: UseAntiforgery() e' attivo e ogni altra POST lo invia (client.ts imposta
  // X-XSRF-TOKEN su tutte le scritture). Senza questo, questa sarebbe l'unica POST scoperta, e si
  // romperebbe al primo irrigidimento della validazione.
  try {
    const tokenRes = await fetch('/api/antiforgery/token', { credentials: 'include' })
    if (tokenRes.ok) {
      const data = (await tokenRes.json()) as { token?: string }
      if (data.token) headers.set('X-XSRF-TOKEN', data.token)
    }
  } catch {
    // Un token non ottenibile non deve impedire il tentativo: il server decide.
  }

  const res = await fetch('/api/contracts/outcome-imports', {
    method: 'POST',
    credentials: 'include',
    headers,
    body: csvText,
  })

  if (!res.ok) {
    const err = (await res.json().catch(() => ({ title: res.statusText }))) as {
      title?: string
      message?: string
      error?: string
    }
    throw new Error(err.title ?? err.message ?? err.error ?? `HTTP ${res.status}`)
  }

  return res.json() as Promise<OutcomeImportPreview>
}

/**
 * Map outcome classifications to human-readable labels. The classifications come from the server:
 * Applicabile, RegistratoNonApplicato, TransizioneRiservata, GiaNelloStato, NonTrovato, Duplicato,
 * EsitoSconosciuto, RichiedeFinanza.
 */
const OUTCOME_LABELS: Record<string, string> = {
  Applicabile: 'Applicabile',
  RegistratoNonApplicato: 'Registrato, non applicato',
  TransizioneRiservata: 'Transizione riservata',
  GiaNelloStato: 'Gia nello stato',
  NonTrovato: 'Contratto non trovato',
  Duplicato: 'Duplicato',
  EsitoSconosciuto: 'Esito sconosciuto',
  RichiedeFinanza: 'Richiede finanza',
}

const OUTCOME_COLORS: Record<string, PillTone> = {
  Applicabile: 'success',
  RegistratoNonApplicato: 'info',
  TransizioneRiservata: 'warning',
  GiaNelloStato: 'info',
  NonTrovato: 'error',
  Duplicato: 'warning',
  EsitoSconosciuto: 'error',
  RichiedeFinanza: 'warning',
}

/**
 * Tab 1: "Import" — CSV upload, preview table, apply button, import history.
 */
function ImportTab() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [preview, setPreview] = useState<OperatorOutcomeImport | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const importExcel = useFeatureFlag('import.excel')

  const handleFileSelect = useCallback(
    async (file: File) => {
      setLoading(true)
      setError(null)
      setUnauthorized(false)
      setPreview(null)

      const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB
      if (file.size > MAX_FILE_SIZE) {
        setError(
          `File troppo grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Limite massimo: 5 MB.`
        )
        setLoading(false)
        return
      }

      try {
        // F-140: un .xlsx e' convertito dal server in testo CSV; da qui in poi e' l'import di sempre.
        const text = await readTabularFile(file)
        const result = await createOutcomeImportPreview(text)
        const importData = await outcomeImportsApi.getImport(result.importId)
        setPreview(importData)
      } catch (e) {
        if (isForbidden(e)) {
          setUnauthorized(true)
        } else {
          setError(e instanceof Error ? e.message : 'Errore')
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const handleApply = useCallback(async () => {
    if (!preview) return
    setLoading(true)
    setError(null)
    try {
      await outcomeImportsApi.applyImport(preview.id)
      const updated = await outcomeImportsApi.getImport(preview.id)
      setPreview(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [preview])

  if (unauthorized) {
    return (
      <Callout tone="warning" role="alert">
        Non sei autorizzato a importare esiti operatore.
      </Callout>
    )
  }

  return (
    <Page>
      {error && (
        <Callout
          tone="error"
          action={<IconButton icon="close" aria-label="Chiudi" size="sm" onClick={() => setError(null)} />}
        >
          {error}
        </Callout>
      )}

      <Card variant="outlined">
        <CardHeader title="Carica CSV" />
        <CardContent>
          <div className="mb-3">
            <input
              ref={fileInputRef}
              type="file"
              aria-label="Carica CSV"
              accept={importExcel ? EXCEL_ACCEPT : CSV_ACCEPT}
              disabled={loading}
              onChange={e => {
                const file = e.currentTarget.files?.[0]
                if (file) void handleFileSelect(file)
              }}
            />
          </div>
          {loading && <CircularProgress size="sm" label="Elaborazione..." />}
        </CardContent>
      </Card>

      {preview && (
        <Card variant="outlined">
          <CardHeader title={`Anteprima: ${preview.rowCount} righe`} />
          <CardContent>
            {!preview.rows || preview.rows.length === 0 ? (
              <Callout tone="info">Nessuna riga in questo file.</Callout>
            ) : (
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th className="md-u-w-60">Riga</th>
                    <th>Chiave</th>
                    <th>Esito dichiarato</th>
                    <th>Classificazione</th>
                    <th className="md-u-w-80">Contratto</th>
                    <th>Tipo match</th>
                    <th className="md-u-w-100">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows?.map(row => (
                    <tr key={row.id}>
                      <td className="md-num">{row.rowNumber}</td>
                      <td className="font-monospace md-typescale-body-small">{row.referenceKey || '—'}</td>
                      <td className="md-typescale-body-small">{row.esito || '—'}</td>
                      <td>
                        <StatusPill tone={OUTCOME_COLORS[row.rowOutcome] || 'neutral'}>
                          {OUTCOME_LABELS[row.rowOutcome] || row.rowOutcome}
                        </StatusPill>
                      </td>
                      <td className="md-typescale-body-small">{row.matchedContractId || '—'}</td>
                      <td className="md-typescale-body-small">{row.matchKind || '—'}</td>
                      <td>
                        {row.appliedAt ? (
                          <StatusPill tone="success">Applicato</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">Preview</StatusPill>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}

            {preview.appliedAt ? (
              <Callout tone="info" className="mt-3">
                Import applicato il {fmtDateTime(preview.appliedAt)}
              </Callout>
            ) : (
              <Button
                variant="tonal"
                size="sm"
                className="mt-3"
                onClick={() => void handleApply()}
                disabled={loading}
              >
                Applica
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </Page>
  )
}

/**
 * Tab 2: "Da riconciliare" — anomalies queue with resolve actions.
 */
function AnomaliesTab() {
  const [anomalies, setAnomalies] = useState<OutcomeAnomalyRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [resolving, setResolving] = useState<number | null>(null)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkingRowId, setLinkingRowId] = useState<number | null>(null)
  const [linkContractId, setLinkContractId] = useState('')
  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false)
  const [ignoringRowId, setIgnoringRowId] = useState<number | null>(null)
  const [ignoreReason, setIgnoreReason] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setUnauthorized(false)
    try {
      const rows = await outcomeImportsApi.getAnomalies()
      setAnomalies(rows)
    } catch (e) {
      if (isForbidden(e)) {
        setUnauthorized(true)
      } else {
        setError(e instanceof Error ? e.message : 'Errore')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleLinkClick = (rowId: number) => {
    setLinkingRowId(rowId)
    setLinkContractId('')
    setLinkModalOpen(true)
  }

  const handleIgnoreClick = (rowId: number) => {
    setIgnoringRowId(rowId)
    setIgnoreReason('')
    setIgnoreModalOpen(true)
  }

  const handleLinkSubmit = useCallback(async () => {
    if (!linkingRowId || !linkContractId) return
    const contractId = parseInt(linkContractId)
    if (isNaN(contractId)) {
      setError('ID contratto non valido. Inserisci un numero.')
      return
    }
    setResolving(linkingRowId)
    try {
      await outcomeImportsApi.resolveAnomaly(linkingRowId, contractId)
      setLinkModalOpen(false)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setResolving(null)
    }
  }, [linkingRowId, linkContractId, load])

  const handleIgnoreSubmit = useCallback(async () => {
    if (!ignoringRowId || !ignoreReason.trim()) return
    setResolving(ignoringRowId)
    try {
      await outcomeImportsApi.resolveAnomaly(ignoringRowId, undefined, ignoreReason)
      setIgnoreModalOpen(false)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setResolving(null)
    }
  }, [ignoringRowId, ignoreReason, load])

  if (unauthorized) {
    return (
      <Callout tone="warning" role="alert">
        Non sei autorizzato a riconciliare anomalie.
      </Callout>
    )
  }

  if (loading && anomalies.length === 0) {
    return <LoadingState label="Caricamento..." />
  }

  if (anomalies.length === 0) {
    return (
      <Callout tone="success" role="alert">
        Niente in coda. Tutti gli esiti sono stati riconciliati.
      </Callout>
    )
  }

  return (
    <Page>
      {error && (
        <Callout
          tone="error"
          action={<IconButton icon="close" aria-label="Chiudi" size="sm" onClick={() => setError(null)} />}
        >
          {error}
        </Callout>
      )}

      <DataTable>
        <thead>
          <tr>
            <th className="md-u-w-60">Import</th>
            <th className="md-u-w-48">Riga</th>
            <th>Chiave</th>
            <th>Esito</th>
            <th>Classificazione</th>
            <th>Motivo</th>
            <th className="md-u-w-180">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {anomalies.map(row => (
            <tr key={row.id}>
              <td className="md-typescale-body-small md-num">{row.importId}</td>
              <td className="md-typescale-body-small md-num">{row.rowNumber}</td>
              <td className="font-monospace md-typescale-body-small">{row.referenceKey || '—'}</td>
              <td className="md-typescale-body-small">{row.esito || '—'}</td>
              <td>
                <StatusPill tone={OUTCOME_COLORS[row.rowOutcome] || 'neutral'}>
                  {OUTCOME_LABELS[row.rowOutcome] || row.rowOutcome}
                </StatusPill>
              </td>
              <td className="md-typescale-body-small md-u-maxw-200 md-u-clip">
                {row.note || '—'}
              </td>
              <td>
                <Button
                  variant="outlined"
                  size="sm"
                  className="me-2"
                  onClick={() => handleLinkClick(row.id)}
                  disabled={resolving === row.id}
                >
                  Collega
                </Button>
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() => handleIgnoreClick(row.id)}
                  disabled={resolving === row.id}
                >
                  Ignora
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      {/* Link Modal */}
      <Dialog
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        dismissible={resolving === null}
        title="Collega a contratto"
        as="form"
        onSubmit={e => { e.preventDefault(); void handleLinkSubmit() }}
        actions={
          <>
            <Button
              type="button"
              variant="text"
              onClick={() => setLinkModalOpen(false)}
              disabled={resolving !== null}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              disabled={!linkContractId || resolving !== null}
            >
              Collega e applica
            </Button>
          </>
        }
      >
        <FormGrid columns={1}>
          <TextField
            label="ID Contratto"
            type="text"
            value={linkContractId}
            onChange={e => setLinkContractId(e.target.value)}
            disabled={resolving !== null}
          />
        </FormGrid>
      </Dialog>

      {/* Ignore Modal */}
      <Dialog
        open={ignoreModalOpen}
        onClose={() => setIgnoreModalOpen(false)}
        dismissible={resolving === null}
        title="Ignora riga"
        as="form"
        onSubmit={e => { e.preventDefault(); void handleIgnoreSubmit() }}
        actions={
          <>
            <Button
              type="button"
              variant="text"
              onClick={() => setIgnoreModalOpen(false)}
              disabled={resolving !== null}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              disabled={!ignoreReason.trim() || resolving !== null}
            >
              Ignora
            </Button>
          </>
        }
      >
        <FormGrid columns={1}>
          <TextField
            label="Motivazione"
            multiline
            rows={4}
            value={ignoreReason}
            onChange={e => setIgnoreReason(e.target.value)}
            placeholder="Descrivi il motivo per cui questa riga viene ignorata"
            disabled={resolving !== null}
          />
        </FormGrid>
      </Dialog>
    </Page>
  )
}

/**
 * Main page: two tabs, "Import" and "Da riconciliare".
 */
export function OutcomeImportsPage() {
  const [activeTab, setActiveTab] = useState<'import' | 'anomalies'>('import')

  return (
    <Page>
      <PageHeader
        title="Import esiti operatore"
        icon="upload_file"
        tabs={
          <Tabs
            aria-label="Sezioni import esiti"
            items={[
              { id: 'import', label: 'Import' },
              { id: 'anomalies', label: 'Da riconciliare' },
            ]}
            value={activeTab}
            onChange={id => setActiveTab(id as 'import' | 'anomalies')}
          />
        }
      />

      {activeTab === 'import' && <ImportTab />}
      {activeTab === 'anomalies' && <AnomaliesTab />}
    </Page>
  )
}
